"use client";

import React, { useState } from "react";
import { motion } from "framer-motion";
import { Shield, Lock, Scale, Clock, ArrowLeft, BookOpen, AlertCircle, HelpCircle } from "lucide-react";
import Link from "next/link";

export default function TermsPage() {
  const [activeTab, setActiveTab] = useState<number>(0);

  const sections = [
    {
      title: "1. Platform Role",
      summary: "Understanding FixerLocal's role as a marketplace, not a service provider.",
      icon: <Shield className="text-emerald-500" />,
      content: "FixerLocal is a marketplace that connects customers with independent local contractors. We are not ourselves a tradesperson, and we do not perform the work booked through the platform. Contractors are independent business operators, not employees or agents of FixerLocal, and are solely responsible for the quality, safety, and legality of the work they perform."
    },
    {
      title: "2. Quotes, Bids & Payment",
      summary: "How pricing works and when you're actually charged.",
      icon: <Lock className="text-blue-500" />,
      content: "When you describe a job, our system gives you an AI-estimated price range for budgeting purposes only - it is not a fixed quote or a guaranteed price. Contractors then submit their own bids with their price and availability; you choose which bid to accept, and that becomes the agreed price. There is no upfront payment or escrow. You are charged via Stripe only after you confirm the job is complete, for the accepted bid amount plus any additional parts or materials you individually approved."
    },
    {
      title: "3. Contractor Listings & Verification",
      summary: "What it means when a contractor is listed vs. verified on FixerLocal.",
      icon: <Scale className="text-amber-500" />,
      content: "Some contractors register and verify their details directly with us. Others' business listings (name, trade, and publicly available contact details) may be sourced from public business directories to help customers discover local trades - these listings are not independently vetted by FixerLocal until the contractor verifies their account. We do not guarantee the licensing, insurance, or qualifications of any contractor; customers should confirm relevant credentials directly with the contractor before work begins."
    },
    {
      title: "4. Cancellations",
      summary: "General principles for cancelling a job.",
      icon: <Clock className="text-rose-500" />,
      content: "You may cancel a job at any time before accepting a contractor's bid at no cost, since no payment has been taken. Once you've accepted a bid and work is underway, cancellation terms (including any compensation for work already completed) should be agreed directly with your contractor. Contact our support team if you need help resolving a cancellation."
    },
    {
      title: "5. Platform Liability & Disputes",
      summary: "Limitations of our liability and how disputes are handled.",
      icon: <AlertCircle className="text-purple-500" />,
      content: "To the extent permitted by Australian Consumer Law, FixerLocal is not liable for indirect or consequential loss arising from work performed by a contractor, or from inaccuracies in AI-estimated pricing. Nothing in these terms limits any consumer guarantee you're entitled to under the Australian Consumer Law that cannot lawfully be excluded. If a dispute arises, contact our support team with details of the job and we'll review it - including any photos, messages, or bid history attached to the job - and work with both parties toward a resolution."
    }
  ];

  return (
    <main className="min-h-screen bg-mesh pb-24 pt-32">
      <div className="mx-auto max-w-5xl px-6">
        
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-600 text-[10px] font-black uppercase tracking-widest mb-6 border border-emerald-100">
            <BookOpen size={12} />
            Legal Agreement Rules
          </div>
          <h1 className="text-5xl font-black tracking-tight text-slate-950 mb-6 italic">
            Terms of Service
          </h1>
          <p className="text-lg text-slate-500 font-medium max-w-2xl mx-auto">
            These terms explain how the FixerLocal marketplace works, how pricing and payment work, and what to expect from contractor listings.
          </p>
        </motion.div>

        {/* Dynamic Sidebar / Tabs Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start mb-16">
          
          {/* Tabs Menu */}
          <div className="lg:col-span-4 lg:sticky lg:top-28 space-y-3">
            <p className="text-xs font-black text-slate-400 uppercase tracking-widest px-4 mb-2">Sections</p>
            {sections.map((sec, idx) => (
              <button
                key={idx}
                onClick={() => setActiveTab(idx)}
                className={`w-full p-4 rounded-2xl border text-left flex items-center gap-4 transition-all ${
                  activeTab === idx
                    ? "bg-slate-950 border-slate-950 text-white shadow-lg"
                    : "bg-white/40 border-white/50 text-slate-600 hover:bg-white hover:border-slate-200"
                }`}
              >
                <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${
                  activeTab === idx ? "bg-white/10 text-emerald-400" : "bg-white text-slate-700 shadow-sm"
                }`}>
                  {sec.icon}
                </div>
                <div className="overflow-hidden">
                  <p className={`text-xs font-bold truncate ${activeTab === idx ? "text-white" : "text-slate-900"}`}>
                    {sec.title}
                  </p>
                  <p className={`text-[10px] truncate font-medium mt-0.5 ${activeTab === idx ? "text-slate-400" : "text-slate-400"}`}>
                    {sec.summary}
                  </p>
                </div>
              </button>
            ))}
          </div>

          {/* Active section Card */}
          <div className="lg:col-span-8">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="glass p-10 md:p-12 rounded-[3.5rem] border border-white/60 shadow-xl relative min-h-[400px] flex flex-col justify-between"
            >
              <div>
                <div className="h-16 w-16 rounded-2xl bg-white shadow-inner flex items-center justify-center border border-slate-50 mb-8">
                  {sections[activeTab].icon}
                </div>
                <h2 className="text-3xl font-black text-slate-950 mb-6 italic">
                  {sections[activeTab].title}
                </h2>
                <p className="text-slate-600 font-medium leading-[1.9] italic text-base">
                  {sections[activeTab].content}
                </p>
              </div>

              <div className="mt-12 pt-8 border-t border-slate-100 flex items-center justify-between text-xs font-bold text-slate-400">
                <span className="uppercase tracking-widest">Section {activeTab + 1} of {sections.length}</span>
                <button
                  onClick={() => setActiveTab((prev) => (prev + 1) % sections.length)}
                  className="text-emerald-600 hover:text-emerald-700 flex items-center gap-1 group"
                >
                  Next Section
                  <ArrowLeft size={14} className="rotate-180 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </motion.div>
          </div>

        </div>

        {/* Dispute Center Info box */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="p-10 md:p-12 rounded-[3rem] bg-slate-950 text-white shadow-2xl relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 p-8 opacity-10">
            <HelpCircle size={120} />
          </div>
          <h2 className="text-2xl font-black mb-6 italic">Need Assistance with an Active Job?</h2>
          <p className="text-slate-400 font-medium leading-relaxed mb-8 max-w-3xl">
            If you have an issue with a booking, payment, or a contractor's added parts, contact our support team with your job details and we'll help sort it out.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link href="/privacy" className="px-6 py-3.5 rounded-xl bg-white/10 hover:bg-white/20 text-xs font-black uppercase tracking-widest transition-all">
              Privacy Policy
            </Link>
            <Link href="/help" className="px-6 py-3.5 rounded-xl bg-white/10 hover:bg-white/20 text-xs font-black uppercase tracking-widest transition-all">
              Help Center
            </Link>
            <a href="mailto:info@fixerlocal.com" className="px-6 py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-xs font-black uppercase tracking-widest transition-all">
              Contact Support
            </a>
          </div>
        </motion.div>

        {/* Back navigation link */}
        <div className="mt-16 text-center">
          <Link href="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-emerald-600 transition-colors text-sm font-bold uppercase tracking-widest">
            <ArrowLeft size={16} />
            Back to Home
          </Link>
        </div>

      </div>
    </main>
  );
}
